import { Box, Flex, type FlexProps, Text } from '@rocket/ui';
import type { FC } from 'react';
import { BiLoaderAlt } from 'react-icons/bi';

export interface ProgressIndicatorProps extends FlexProps {
  message: string;
  current: number;
  total: number;
  percentComplete: number;
}

/**
 * Visual progress indicator for async operations.
 * Shows a progress bar, spinner, and status message.
 */
export const ProgressIndicator: FC<ProgressIndicatorProps> = ({
  message,
  current,
  total,
  percentComplete,
  ...props
}) => {
  return (
    <Flex.V
      gap="12px"
      padding="16px"
      colorPalette="blue"
      bg="blue.subtle"
      borderRadius="4px"
      {...props}
    >
      <Flex.H alignItems="center" gap="12px">
        <BiLoaderAlt className="spin" size={20} />
        <Text fontWeight={600} fontSize="sm" color="blue.fg">
          {message}
        </Text>
      </Flex.H>

      {/* Progress Bar */}
      <Box width="100%" height="8px" bg="bg.muted" borderRadius="4px" overflow="hidden">
        <Box
          height="100%"
          width={`${Math.min(percentComplete, 100)}%`}
          bg="blue.solid"
          style={{ transition: 'width 0.3s ease' }}
        />
      </Box>

      {/* Progress Text */}
      {total > 0 && (
        <Flex.H justifyContent="space-between">
          <Text fontSize="xs" color="fg.muted">
            {current} of {total} items
          </Text>
          <Text fontSize="xs" color="fg.muted" fontWeight={600}>
            {Math.round(percentComplete)}%
          </Text>
        </Flex.H>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Flex.V>
  );
};
